import React, { useCallback, useState } from 'react'
import { Separator, ItemPress, ListItem, TextList, Right } from '../components/List'
import { Linking, View, Text, TextInput, Alert, StyleSheet } from 'react-native'
import MaterialCommunityIcons from 'react-native-vector-icons/MaterialCommunityIcons'
import { useAppDispatch, useAppSelector } from '#app/hooks'
import { t, gettext } from 'ttag'
import { resetCurrentCentral } from '#app/central'
import { userLogout } from '#app/actions'
import { selectSelectedTheme, useAppTheme, toggleSelectedTheme } from '#app/theme'
import { selectCurrentUser } from '#app/user'
import { selectCan, selectCurrentUserCentral, setUserCentralLocalIpAddress } from '#app/centraluser'
import { useNavigation } from '@react-navigation/native'

export default function Settings () {
  const THEME_TYPES = {
    system: gettext('System'),
    dark: gettext('Dark'),
    light: gettext('Light')
  }

  const dispatch = useAppDispatch()
  const navigation = useNavigation()
  const theme = useAppTheme()
  const user = useAppSelector(selectCurrentUser)
  const central = useAppSelector(selectCurrentUserCentral)
  const selectedTheme = useAppSelector(selectSelectedTheme)
  const can = useAppSelector(selectCan)
  const canEdit = can('edit')

  // Local connection state
  const [localIpInput, setLocalIpInput] = useState(central.localIpAddress ?? '')
  const isLocalConnectionEnabled = central.localIpAddress != null && central.localIpAddress !== ''

  const validateIpAddress = (ip: string): boolean => {
    if (ip === '') return true // Empty is valid (disables local connection)

    // IPv4 validation
    const ipv4Pattern = /^(\d{1,3}\.){3}\d{1,3}$/
    if (ipv4Pattern.test(ip)) {
      const parts = ip.split('.')
      return parts.every(part => {
        const num = parseInt(part, 10)
        return num >= 0 && num <= 255
      })
    }

    // IPv6 validation (including Yggdrasil addresses like 200:xxxx:xxxx:...)
    // Matches full, compressed (::), and mixed formats
    const ipv6Pattern = /^([0-9a-fA-F]{1,4}:){7}[0-9a-fA-F]{1,4}$|^([0-9a-fA-F]{1,4}:){1,7}:$|^([0-9a-fA-F]{1,4}:){1,6}:[0-9a-fA-F]{1,4}$|^([0-9a-fA-F]{1,4}:){1,5}(:[0-9a-fA-F]{1,4}){1,2}$|^([0-9a-fA-F]{1,4}:){1,4}(:[0-9a-fA-F]{1,4}){1,3}$|^([0-9a-fA-F]{1,4}:){1,3}(:[0-9a-fA-F]{1,4}){1,4}$|^([0-9a-fA-F]{1,4}:){1,2}(:[0-9a-fA-F]{1,4}){1,5}$|^[0-9a-fA-F]{1,4}:(:[0-9a-fA-F]{1,4}){1,6}$|^:(:[0-9a-fA-F]{1,4}){1,7}$|^::$|^fe80:(:[0-9a-fA-F]{0,4}){0,4}%[0-9a-zA-Z]+$|^::(ffff(:0{1,4})?:)?(\d{1,3}\.){3}\d{1,3}$|^([0-9a-fA-F]{1,4}:){1,4}:(\d{1,3}\.){3}\d{1,3}$/
    return ipv6Pattern.test(ip)
  }

  const onSaveLocalIp = useCallback(() => {
    const trimmedIp = localIpInput.trim()
    if (!validateIpAddress(trimmedIp)) {
      Alert.alert(
        t`Invalid IP Address`,
        t`Please enter a valid IPv4 (e.g., 192.168.1.100) or IPv6/Yggdrasil address (e.g., 200:abcd::1), or leave empty to disable local connection.`
      )
      return
    }
    dispatch(setUserCentralLocalIpAddress({
      id: central.id,
      localIpAddress: trimmedIp === '' ? undefined : trimmedIp
    }))
    Alert.alert(
      t`Local Connection`,
      trimmedIp === ''
        ? t`Local connection disabled. Using cloud connection.`
        : t`Local connection configured. The app will connect to ${trimmedIp} for this central.`
    )
  }, [dispatch, central.id, localIpInput])

  const changeCentral = useCallback(() => {
    dispatch(resetCurrentCentral())
  }, [dispatch])

  const logout = useCallback(() => {
    dispatch(userLogout())
  }, [dispatch])

  const onAccessoriesPress = useCallback(() => navigation.navigate('SlaveList'), [navigation])

  const onEnvironmentPress = useCallback(() => navigation.navigate('Environment'), [navigation])

  const onUsersPress = useCallback(() => {
    navigation.navigate('CentralUsers', { id: central.id })
  }, [navigation, central.id])

  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const onSchedulesPress = useCallback(() => navigation.navigate('Schedules'), [])

  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const onScenesPress = useCallback(() => navigation.navigate('SceneList'), [])

  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const onLogsPress = useCallback(() => navigation.navigate('Logs'), [])

  return (
    (<ListItem>
      <Separator text={`Central: ${central.name}`} />
      <ItemPress onPress={changeCentral}>
        <TextList text={t`Change Central`} />
        <Right>
          <MaterialCommunityIcons name="chevron-right" size={20} color={theme.colors.grey} />
        </Right>
      </ItemPress>
      <Separator text={t`Preferences`} />
      {canEdit && (
        <ItemPress onPress={onAccessoriesPress}>
          <TextList text={t`Products`} />
          <Right>
            <MaterialCommunityIcons name="chevron-right" size={20} color={theme.colors.grey} />
          </Right>
        </ItemPress>
      )}
      <ItemPress onPress={onUsersPress}>
        <TextList text={t`Users`} />
        <Right>
          <MaterialCommunityIcons name="chevron-right" size={20} color={theme.colors.grey} />
        </Right>
      </ItemPress>
      {canEdit && (
        <ItemPress onPress={onSchedulesPress}>
          <TextList text={t`Schedules`} />
          <Right>
            <MaterialCommunityIcons name="chevron-right" size={20} color={theme.colors.grey} />
          </Right>
        </ItemPress>
      )}
      <ItemPress onPress={onScenesPress}>
        <TextList text={t`Scenes`} />
        <Right>
          <MaterialCommunityIcons name="chevron-right" size={20} color={theme.colors.grey} />
        </Right>
      </ItemPress>
      {canEdit && <>
        <ItemPress onPress={onEnvironmentPress}>
          <TextList text={t`Environment`} />
          <Right>
            <MaterialCommunityIcons name="chevron-right" size={20} color={theme.colors.grey} />
          </Right>
        </ItemPress>
        <ItemPress onPress={() => navigation.navigate('NodeRed')}>
          <TextList text="Node-RED" />
          <Right>
            <MaterialCommunityIcons name="chevron-right" size={20} color={theme.colors.grey} />
          </Right>
        </ItemPress>
      </>}
      <ItemPress onPress={() => dispatch(toggleSelectedTheme())}>
        <TextList text={t`Theme`} />
        <View style={{ flex: 1, alignItems: 'flex-end', justifyContent: 'center' }}>
          <Text style={{ color: theme.textColor }}>{THEME_TYPES[selectedTheme]}</Text>
        </View>
      </ItemPress>
      <Separator text={t`Others`} />
      {
        canEdit && (
          <ItemPress onPress={onLogsPress}>
            <TextList text={t`Logs`} />
            <Right>
              <MaterialCommunityIcons name="chevron-right" size={20} color={theme.colors.grey} />
            </Right>
          </ItemPress>
        )
      }
      <Separator text={t`Local Connection`} />
      <View style={styles.localConnectionContainer}>
        <View style={styles.localConnectionRow}>
          <Text style={[styles.localConnectionLabel, { color: theme.textColor }]}>{t`Gateway IP`}</Text>
          <View style={styles.localConnectionStatus}>
            <MaterialCommunityIcons
              name={isLocalConnectionEnabled ? 'lan-connect' : 'lan-disconnect'}
              size={20}
              color={isLocalConnectionEnabled ? '#4CAF50' : theme.colors.grey}
            />
            <Text style={{ color: isLocalConnectionEnabled ? '#4CAF50' : theme.colors.grey, marginLeft: 4 }}>
              {isLocalConnectionEnabled ? t`Local` : t`Cloud`}
            </Text>
          </View>
        </View>
        <TextInput
          style={[styles.localConnectionInput, { color: theme.textColor, borderColor: theme.colors.grey }]}
          placeholder={t`e.g., 192.168.1.100 or 200:abcd::1`}
          placeholderTextColor={theme.colors.grey}
          value={localIpInput}
          onChangeText={setLocalIpInput}
          keyboardType="default"
          autoCapitalize="none"
          autoCorrect={false}
        />
        <Text style={[styles.localConnectionHint, { color: theme.colors.grey }]}>
          {t`Enter the local IP (IPv4) or Yggdrasil address (IPv6) of your gateway. Leave empty to use cloud connection.`}
        </Text>
        <ItemPress onPress={onSaveLocalIp}>
          <TextList text={t`Save Local IP`} />
          <Right>
            <MaterialCommunityIcons name="content-save" size={20} color={theme.colors.grey} />
          </Right>
        </ItemPress>
      </View>
      <Separator text={t`My Profile`} />
      <ItemPress onPress={() => { }}>
        <TextList text={user.name} />
        <Right>
          <MaterialCommunityIcons name="chevron-right" size={20} color={theme.colors.grey} />
        </Right>
      </ItemPress>
      <ItemPress onPress={async () => await Linking.openURL('mailto:suporte@myio.com.br')}>
        <TextList text={t`Email Support`} />
        <Right>
          <MaterialCommunityIcons name="chevron-right" size={20} color={theme.colors.grey} />
        </Right>
      </ItemPress>
      <ItemPress onPress={logout}>
        <TextList text={t`Logout`} />
        <Right>
          <MaterialCommunityIcons name="chevron-right" size={20} color={theme.colors.grey} />
        </Right>
      </ItemPress>
    </ListItem>)
  )
}

const styles = StyleSheet.create({
  localConnectionContainer: {
    paddingHorizontal: 16,
    paddingVertical: 8
  },
  localConnectionRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8
  },
  localConnectionLabel: {
    fontSize: 14,
    fontWeight: '500'
  },
  localConnectionStatus: {
    flexDirection: 'row',
    alignItems: 'center'
  },
  localConnectionInput: {
    borderWidth: 1,
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 10,
    fontSize: 16,
    marginBottom: 8
  },
  localConnectionHint: {
    fontSize: 12,
    marginBottom: 8
  }
})
